import * as THREE from 'three';
import type { EditorDoc, CeilingProfile } from '@roomtreatment/core';
import type { PolyPt } from '@roomtreatment/floorplan-core';
import {
  ceilingHasSlope,
  ceilingHeightAt,
  profileBreakUs,
  ceilingIsSmooth,
  type RoomBBox,
} from '@roomtreatment/floorplan-core';
import { getRoomPolygon } from './roomPolygon';
import type { SurfaceFrame } from './panelSurface';

export interface SurfaceOutlineContext {
  poly: PolyPt[];
  bbox: RoomBBox;
  ceiling: CeilingProfile | null;
  H: number;
  sloped: boolean;
}

function polyBounds(poly: PolyPt[]): RoomBBox {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of poly) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, maxX, minY, maxY };
}

function ceilAt(ctx: SurfaceOutlineContext, x: number, z: number): number {
  if (!ctx.sloped || !ctx.ceiling) return ctx.H;
  const { minX, maxX, minY, maxY } = ctx.bbox;
  const tx = maxX - minX > 1e-6 ? (x - minX) / (maxX - minX) : 0;
  const ty = maxY - minY > 1e-6 ? (z - minY) / (maxY - minY) : 0;
  return ceilingHeightAt(ctx.ceiling, tx, ty);
}

function topFractions(
  a: PolyPt,
  b: PolyPt,
  ceiling: CeilingProfile | null,
  bbox: RoomBBox,
  sloped: boolean,
): number[] {
  if (!sloped || !ceiling) return [0, 1];
  const smooth = ceilingIsSmooth(ceiling);
  const breaks = profileBreakUs(a, b, ceiling, bbox);
  const us: number[] = [];
  for (let i = 0; i < breaks.length - 1; i++) {
    const u0 = breaks[i]!;
    const u1 = breaks[i + 1]!;
    const sub = smooth ? 6 : 1;
    for (let s = 0; s < sub; s++) us.push(u0 + (u1 - u0) * (s / sub));
  }
  us.push(1);
  return us;
}

/** Host-surface outline in the surface's (u, v) frame — matches RoomSurfaces. */
export function surfaceOutlineUv(
  frame: SurfaceFrame,
  ctx: SurfaceOutlineContext,
): PolyPt[] {
  if (frame.ref.kind === 'corner') {
    return [
      { x: 0, y: 0 },
      { x: frame.uMax, y: 0 },
      { x: frame.uMax, y: frame.vMax },
      { x: 0, y: frame.vMax },
    ];
  }

  if (frame.ref.kind === 'wall') {
    const start = frame.origin;
    const end = frame.origin.clone().addScaledVector(frame.u, frame.uMax);
    const a: PolyPt = { x: start.x, y: start.z };
    const b: PolyPt = { x: end.x, y: end.z };
    const outline: PolyPt[] = [
      { x: 0, y: 0 },
      { x: frame.uMax, y: 0 },
    ];
    const fr = topFractions(a, b, ctx.ceiling, ctx.bbox, ctx.sloped);
    for (let i = fr.length - 1; i >= 0; i--) {
      const t = fr[i]!;
      const x = a.x + (b.x - a.x) * t;
      const z = a.y + (b.y - a.y) * t;
      outline.push({ x: t * frame.uMax, y: ceilAt(ctx, x, z) });
    }
    return outline;
  }

  const outline = ctx.poly.map((p) => {
    const rel = new THREE.Vector3(p.x, frame.origin.y, p.y).sub(frame.origin);
    return { x: rel.dot(frame.u), y: rel.dot(frame.v) };
  });
  let area = 0;
  for (let i = 0; i < outline.length; i++) {
    const p = outline[i]!;
    const q = outline[(i + 1) % outline.length]!;
    area += p.x * q.y - q.x * p.y;
  }
  if (area < 0) outline.reverse();
  return outline;
}

export function surfaceOutlineContextFromDoc(doc: EditorDoc): SurfaceOutlineContext {
  const poly = getRoomPolygon(doc);
  return {
    poly,
    bbox: polyBounds(poly),
    ceiling: doc.ceiling ?? null,
    H: doc.dims.H,
    sloped: ceilingHasSlope(doc.ceiling ?? null),
  };
}

export function surfaceOutlineFromDoc(frame: SurfaceFrame, doc: EditorDoc): PolyPt[] {
  return surfaceOutlineUv(frame, surfaceOutlineContextFromDoc(doc));
}

export function outlineBounds(outline: PolyPt[]): {
  uMin: number; uMax: number; vMin: number; vMax: number;
} {
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const p of outline) {
    uMin = Math.min(uMin, p.x);
    uMax = Math.max(uMax, p.x);
    vMin = Math.min(vMin, p.y);
    vMax = Math.max(vMax, p.y);
  }
  if (!Number.isFinite(uMin)) {
    return { uMin: 0, uMax: 0, vMin: 0, vMax: 0 };
  }
  return { uMin, uMax, vMin, vMax };
}
